import asanaLogo from "@static/img/asana-logo.png";
import clickupLogo from "@static/img/clickup.png";
import firefliesLogo from "@static/img/fireflies-logo.png";
import githubLogo from "@static/img/github.png";
import granolaLogo from "@static/img/granola-logo.png";
import hubspotLogo from "@static/img/hubspot.png";
import jiraLogo from "@static/img/jira.png";
import readAiLogo from "@static/img/read-ai-logo.svg";
import salesforceLogo from "@static/img/salesforce.png";
import zoomLogo from "@static/img/zoom.png";

import { GetAccountQuery } from "../../../../types/graphql-schema";

type Account = NonNullable<GetAccountQuery["me"]>;

export type PersonalIntegrationConnectKind =
  | { kind: "url"; url: string }
  | { kind: "transcriptToken"; provider: "granola" | "fireflies" };

export type PersonalIntegrationDefinition = {
  slug: string;
  name: string;
  logo: string;
  summary: string;
  description: JSX.Element;
  categories: readonly string[];
  isConnected: (account: Account) => boolean;
  connect: PersonalIntegrationConnectKind;
};

export const PERSONAL_INTEGRATIONS: readonly PersonalIntegrationDefinition[] = [
  {
    slug: "zoom",
    name: "Zoom",
    logo: zoomLogo,
    summary:
      "Add Zoom meeting links automatically to meetings you create in Topicflow.",
    description: (
      <>
        <p>
          Add Zoom meetings automatically to meetings you create in Topicflow.
        </p>
        <p className="font-bold uppercase py-2 text-gray-400">Note:</p>
        <ul className="list-disc ml-4 text-gray-400">
          <li>
            An administrator for your Zoom account must pre-approve our{" "}
            <a
              href="https://marketplace.zoom.us/apps/o5pQpYQeT5CCiZcXtdY73w"
              title="Topicflow on Zoom Marketplace"
              className="text-blue-600"
            >
              Zoom Marketplace app
            </a>
            .
          </li>
        </ul>
      </>
    ),
    categories: ["productivity"],
    isConnected: (account) => Boolean(account.hasZoomAuth),
    connect: {
      kind: "url",
      url: `/sso/zoom?next=${encodeURIComponent("/account/integrations?open=zoom")}`,
    },
  },
  {
    slug: "asana",
    name: "Asana",
    logo: asanaLogo,
    summary: "Sync your Topicflow action items into Asana.",
    description: <p>Sync your Topicflow action items into Asana.</p>,
    categories: ["ticketing"],
    isConnected: (account) => Boolean(account.hasAsanaAuth),
    connect: {
      kind: "url",
      url: `/sso/asana?next=${encodeURIComponent("/account/integrations?open=asana")}`,
    },
  },
  {
    slug: "github",
    name: "GitHub",
    logo: githubLogo,
    summary:
      "Preview GitHub issues and pull requests in meeting notes and enrich your work history.",
    description: (
      <>
        <p>Connect your Topicflow and GitHub accounts.</p>
        <ul className="list-disc ml-4 py-2">
          <li>
            Preview GitHub issues and pull requests in Topicflow meeting notes
          </li>
          <li>
            Search for GitHub issues and pull requests from the Explorer, and
            embed lists in meeting notes
          </li>
          <li>Enrich your work history with GitHub activity</li>
        </ul>
      </>
    ),
    categories: ["ticketing"],
    isConnected: (account) => Boolean(account.hasGithubAuth),
    connect: {
      kind: "url",
      url: "/sso/github?next=%2Faccount%2Fintegrations%3Fopen%3Dgithub",
    },
  },
  {
    slug: "jira",
    name: "Jira",
    logo: jiraLogo,
    summary:
      "Preview Jira issues in meeting notes and embed them from the Explorer.",
    description: (
      <>
        <p>Connect your Topicflow and Jira accounts.</p>
        <ul className="list-disc ml-4 py-2">
          <li>Preview Jira issues in Topicflow meeting notes</li>
          <li>
            Search for Jira issues from the Explorer, and embed lists in meeting
            notes
          </li>
        </ul>
      </>
    ),
    categories: ["ticketing"],
    isConnected: (account) => Boolean(account.hasJiraAuth),
    connect: {
      kind: "url",
      url: `/sso/jira?next=${encodeURIComponent("/account/integrations?open=jira")}`,
    },
  },
  {
    slug: "clickup",
    name: "ClickUp",
    logo: clickupLogo,
    summary: "Preview ClickUp tasks in Topicflow meeting notes.",
    description: (
      <>
        <p>Connect your Topicflow and ClickUp accounts.</p>
        <ul className="list-disc ml-4 py-2">
          <li>Preview ClickUp tasks in Topicflow meeting notes</li>
        </ul>
      </>
    ),
    categories: ["ticketing"],
    isConnected: (account) => Boolean(account.hasClickupAuth),
    connect: {
      kind: "url",
      url: `/sso/clickup?next=${encodeURIComponent("/account/integrations?open=clickup")}`,
    },
  },
  {
    slug: "salesforce",
    name: "Salesforce",
    logo: salesforceLogo,
    summary:
      "Preview Salesforce Opportunities in meeting notes and enrich your work history.",
    description: (
      <>
        <p>Connect your Topicflow and Salesforce accounts.</p>
        <ul className="list-disc ml-4 py-2">
          <li>Preview Salesforce Opportunities in Topicflow meeting notes</li>
          <li>Enrich your work history with Salesforce data</li>
        </ul>
      </>
    ),
    categories: ["crm"],
    isConnected: (account) => Boolean(account.hasSalesforceAuth),
    connect: {
      kind: "url",
      url: `/sso/salesforce?next=${encodeURIComponent("/account/integrations?open=salesforce")}`,
    },
  },
  {
    slug: "hubspot",
    name: "HubSpot",
    logo: hubspotLogo,
    summary:
      "Preview HubSpot Contacts and Deals in meeting notes and enrich your work history.",
    description: (
      <>
        <p>Connect your Topicflow and HubSpot accounts.</p>
        <ul className="list-disc ml-4 py-2">
          <li>Preview HubSpot Contacts and Deals in Topicflow meeting notes</li>
          <li>
            Search for HubSpot Contacts and Deals from the Explorer, and embed
            lists in meeting notes
          </li>
          <li>Enrich your work history with HubSpot data</li>
        </ul>
      </>
    ),
    categories: ["crm"],
    isConnected: (account) => Boolean(account.hasHubspotAuth),
    connect: {
      kind: "url",
      url: `/sso/hubspot?next=${encodeURIComponent("/account/integrations?open=hubspot")}`,
    },
  },
  {
    slug: "granola",
    name: "Granola",
    logo: granolaLogo,
    summary: "Import your Granola meeting transcripts into Topicflow.",
    description: (
      <>
        <p>Import your Granola meeting transcripts into Topicflow.</p>
        <ul className="list-disc ml-4 py-2">
          <li>Import Granola transcripts into matching Topicflow meetings</li>
          <li>Create Topicflow topics from Granola meeting notes</li>
        </ul>
      </>
    ),
    categories: ["productivity"],
    isConnected: (account) => Boolean(account.hasGranolaPersonalApiToken),
    connect: { kind: "transcriptToken", provider: "granola" },
  },
  {
    slug: "fireflies",
    name: "Fireflies",
    logo: firefliesLogo,
    summary: "Import your Fireflies meeting transcripts into Topicflow.",
    description: (
      <>
        <p>Import your Fireflies meeting transcripts into Topicflow.</p>
        <ul className="list-disc ml-4 py-2">
          <li>Import Fireflies transcripts into matching Topicflow meetings</li>
          <li>Create Topicflow topics from Fireflies meeting summaries</li>
        </ul>
      </>
    ),
    categories: ["productivity"],
    isConnected: (account) => Boolean(account.hasFirefliesPersonalApiToken),
    connect: { kind: "transcriptToken", provider: "fireflies" },
  },
  {
    slug: "read-ai",
    name: "Read.AI",
    logo: readAiLogo,
    summary: "Import your Read.AI meeting transcripts into Topicflow.",
    description: (
      <>
        <p>Import your Read.AI meeting transcripts into Topicflow.</p>
        <ul className="list-disc ml-4 py-2">
          <li>Import Read.AI transcripts into matching Topicflow meetings</li>
          <li>Create Topicflow topics from Read.AI meeting summaries</li>
        </ul>
      </>
    ),
    categories: ["productivity"],
    isConnected: (account) => Boolean(account.hasReadAiAuth),
    connect: {
      kind: "url",
      url: `/sso/readai?next=${encodeURIComponent("/account/integrations?open=read-ai")}`,
    },
  },
];

export const getPersonalIntegration = (slug: string) =>
  PERSONAL_INTEGRATIONS.find((i) => i.slug === slug);
